import React from "react";
import type { RailNavItem, SubNavItem } from "./NavData";

interface SubNavProps {
  section: RailNavItem | undefined;
  activePath: string;
  onNavigate: (path: string) => void;
  /** Optional handler — when provided, each pinned item gets a hover × button. */
  onRemovePinned?: (id: string) => void;
}

const SubNav: React.FC<SubNavProps> = ({
  section,
  activePath,
  onNavigate,
  onRemovePinned,
}) => {
  if (!section?.sub || section.sub.length === 0) return null;

  const toneColor = (tone: SubNavItem["tone"]) => {
    if (tone === "warn") return "var(--warn)";
    if (tone === "risk") return "var(--risk)";
    if (tone === "ok") return "var(--ok)";
    if (tone === "info") return "var(--info)";
    return "transparent";
  };

  const hasPinned = !!(section.pinned && section.pinned.length > 0);

  return (
    <aside
      aria-label={`${section.label} navigation`}
      style={{
        gridArea: "subnav",
        background: "var(--bg)",
        borderRight: "1px solid var(--line)",
        width: "var(--subnav-w)",
        height: "100%",
        overflow: "auto",
        padding: "20px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      {/* Section header */}
      <div style={{ padding: "0 8px 14px" }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "var(--ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontFamily: "var(--font-mono)",
            marginBottom: 4,
          }}
        >
          {section.group}
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "var(--ink-1)",
            fontFamily: "var(--font-display, var(--font-sans))",
            lineHeight: 1.2,
          }}
        >
          {section.label}
        </div>
      </div>

      {/* Sub-nav items */}
      <nav
        style={{ display: "flex", flexDirection: "column", gap: 2 }}
        aria-label={`${section.label} sub-navigation`}
      >
        {section.sub.map((s) => {
          const isActive = activePath === s.path;
          const hasTone = !!s.tone;

          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onNavigate(s.path)}
              aria-current={isActive ? "page" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "7px 10px",
                borderRadius: "var(--r-sm)",
                background: isActive ? "var(--accent-soft)" : "transparent",
                border: "1px solid transparent",
                color: isActive ? "var(--accent-soft-ink)" : "var(--ink-1)",
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                cursor: "pointer",
                textAlign: "left",
                transition: "background 100ms",
              }}
              onMouseEnter={(e) => {
                if (!isActive)
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "var(--bg-sunken)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  isActive ? "var(--accent-soft)" : "transparent";
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hasTone && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: toneColor(s.tone),
                      flexShrink: 0,
                    }}
                  />
                )}
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </span>
              </span>
              {typeof s.count === "number" && (
                <span
                  aria-label={`${s.count} items`}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: isActive ? "var(--accent-soft-ink)" : "var(--ink-3)",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                    marginLeft: 8,
                  }}
                >
                  {s.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Divider */}
      <div
        style={{
          height: 1,
          background: "var(--line)",
          margin: "16px 8px",
          flexShrink: 0,
        }}
      />

      {/* Pinned saved-views */}
      {hasPinned && (
        <>
          <div style={{ padding: "0 8px" }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontFamily: "var(--font-mono)",
              }}
            >
              Pinned
            </div>
          </div>
          <nav
            aria-label={`${section.label} pinned views`}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginTop: 8,
            }}
          >
            {section.pinned!.map((p) => {
              const PinnedIcon = p.icon;
              return (
                <div
                  key={p.id}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "stretch",
                  }}
                  onMouseEnter={(e) => {
                    const x =
                      e.currentTarget.querySelector<HTMLElement>(
                        "[data-pin-remove]",
                      );
                    if (x) x.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    const x =
                      e.currentTarget.querySelector<HTMLElement>(
                        "[data-pin-remove]",
                      );
                    if (x) x.style.opacity = "0";
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onNavigate(p.path)}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 8px",
                      paddingRight: onRemovePinned ? 26 : 8,
                      border: "none",
                      background: "transparent",
                      borderRadius: "var(--r-sm)",
                      fontSize: 12,
                      color: "var(--ink-1)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "var(--bg-sunken)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "transparent";
                    }}
                  >
                    {PinnedIcon && (
                      <span
                        aria-hidden="true"
                        style={{
                          color: "var(--ink-3)",
                          display: "inline-flex",
                          flexShrink: 0,
                        }}
                      >
                        <PinnedIcon size={12} />
                      </span>
                    )}
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.label}
                    </span>
                  </button>
                  {onRemovePinned && (
                    <button
                      type="button"
                      data-pin-remove
                      aria-label={`Remove pinned view ${p.label}`}
                      title="Remove pinned view"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemovePinned(p.id);
                      }}
                      style={{
                        position: "absolute",
                        right: 4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 18,
                        height: 18,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        background: "transparent",
                        color: "var(--ink-3)",
                        borderRadius: "var(--r-xs)",
                        cursor: "pointer",
                        fontSize: 12,
                        lineHeight: 1,
                        opacity: 0,
                        transition: "opacity 100ms",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </nav>
        </>
      )}
    </aside>
  );
};

export default SubNav;
